/**
 * @typedef {{
 *	id: string;
 * 	title: string;
 *  price: number;
 *  priceCurrency: string;
 *  totalShares: number;
 *  availableShares: number;
 *  images: string[];
 *  annualizedReturnPercentage: number;
 *  annualizedAppreciationPercentage: number;
 *  projectedGrossYieldPercentage: number;
 *  projectedNetYieldPercentage: number;
 *  isAvailable: boolean;
 *  minInvestmentPerShares: number;
 *  minInvestmentPerSharesCurrency: string;
 *  sharesAmountForMinInvestment: number;
 * }} Property
 */

import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPagination,
  CarouselPrevious,
} from "../UI/Carousel";
import {
  ShowcaseCard,
  ShowcaseCardDetails,
  ShowcaseCardFront,
  ShowcaseCardProvider,
  ShowcaseCardTrigger,
} from "./ShowcaseCard";

import Img from "../UI/Img";
import Link from "next/link";
import { MoveRight } from "lucide-react";
import { cn } from "~/lib/utils";
import { env } from "~/env";
import { minionProFontFamily } from "~/lib/fonts/minion-pro";

// const numberFormatter = new Intl.NumberFormat("en-US", {
//   maximumSignificantDigits: 3,
// });

// // eslint-disable-next-line no-unused-vars
// function formatNumber(num) {
//   return numberFormatter.format(num);
// }

/**
 * @param {{
 * 	data: Property;
 * 	defaultExpanded?: "details" | "front"
 *  className?: string;
 *  imgPriority?: boolean
 * }} props
 *
 * @example
 * ```jsx
 * <div className="flex w-full flex-wrap items-center justify-center gap-16">
 *   {properties.map((property, index) => (
 *     <PropertyCard
 *       key={index}
 *       data={property}
 *       defaultExpanded={index === 1 ? "details" : "front"}
 *       className={index === 1 ? "scale-105 sm:scale-125" : ""}
 *     />
 *   ))}
 * ```
 */
export default function PropertyCard(props) {
  return (
    <ShowcaseCardProvider
      key={props.data.id}
      defaultExpanded={props.defaultExpanded}
    >
      <ShowcaseCard
        className={cn("h-[36rem] xxl:w-[26rem] xl:w-[23rem] lg:w-[22rem] max-w-full w-[18rem]", props.className)}
        style={{
          boxShadow: "0 0rem 1.8rem -0.65rem gray",
        }}
      >
        <ShowcaseCardFront className="relative flex max-w-full flex-col">
          <div className="relative h-[85%] max-w-full group-[.expanded]:h-[85%]">
            <ShowcaseCardTrigger className="bottom-0 end-2 translate-y-1/2" />
            <Carousel className="h-full" opts={{ loop: true }}>
              <CarouselContent className="h-full">
                {props.data.images.slice(0,3).map((image, index) => (
                  <CarouselItem key={image + index} className="h-full">
                    <div className="relative h-full w-full">
                      <Img
                        src={image}
                        alt={props.data.title}
                        width={292.8}
                        height={192}
                        className="h-full w-full object-cover"
                        priority={props.imgPriority && index === 0}
                      />
                      {props.data.isAvailable && (
                        <p
                          className={cn(
                            "absolute bottom-3 left-3 rounded-sm px-3 py-1.5 uppercase",
                            "border-[0.03125rem] border-solid border-white bg-slate-600/70 text-sm text-white hover:bg-slate-600/50 hover:backdrop-blur-[0.0625rem] focus:bg-slate-800/50 focus:backdrop-blur-0",
                          )}
                        >
                          <strong>available</strong>
                        </p>
                      )}
                    </div>
                  </CarouselItem>
                ))}
              </CarouselContent>
              <CarouselPrevious />
              <CarouselNext />
              <CarouselPagination className="absolute left-1/2 top-4 -translate-x-1/2 transform" />
            </Carousel>
          </div>
          <div className="relative flex max-w-full flex-grow flex-col overflow-hidden whitespace-nowrap bg-gradient-to-b from-[#FFFAF8] to-[rgb(255,250,248,0.5)] px-6 pb-0 pt-0 sm:flex-row sm:items-center sm:justify-between">
            <p
              className="inline-block leading-[0]"
              style={{
                width: `75%`,
              }}
            >
              <span
                className={`${minionProFontFamily.className} inline-block max-w-full overflow-hidden text-ellipsis whitespace-nowrap text-base text-[#2c3a5c]`}
              >
                {props.data.title.length > 50
                  ? props.data.title.substring(0, 50) + "..."
                  : props.data.title}
              </span>
            </p>
            {/* <p className="flex-shrink-0 leading-[1]">
              {formatNumber(props.data.price)}{" "}
              <span className="text-gray-500">{props.data.priceCurrency}</span>
            </p> */}
          </div>
        </ShowcaseCardFront>
        <ShowcaseCardDetails className="flex flex-col justify-between gap-2 bg-gradient-to-b from-[rgb(255,250,248,0.5)] to-white sm:gap-4">
          <div className="flex justify-between gap-4 bg-primary px-6 py-4 text-sm ">
            <p className="flex flex-col text-white max-w-[200px]">
              Login or register to view more details and invest.
            </p>
            <div className="flex flex-col items-center justify-center">
              <Link
                href={env.NEXT_PUBLIC_AUTH_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 bg-secondary px-4 py-2 uppercase text-white whitespace-nowrap"
              >
                <strong>Let&apos;s go</strong> <MoveRight />
              </Link>
            </div>
          </div>
        </ShowcaseCardDetails>
      </ShowcaseCard>
    </ShowcaseCardProvider>
  );
}
